/**
 * Concierge Analytics Tracking Types
 *
 * This file defines TypeScript interfaces for all concierge tracking events.
 * These types ensure type-safety when implementing tracking in the web app and mobile app.
 *
 * Event Naming Convention:
 * - Backend (ConciergeAgentEvent): Uses 'concierge:' prefix
 * - Frontend (AnalyticsTrackingEvent): Uses 'CONCIERGE_' prefix with matching event name
 *
 * Implementation Notes:
 * - Web App: Use AnalyticsService.track(AnalyticsTrackingEvent.CONCIERGE_*, properties)
 * - Mobile App: Use the equivalent tracking service with matching event names
 * - sessionId should be consistent within a browsing session (use fingerprint/visitor ID)
 */

// ============================================================================
// Widget Type Definitions
// ============================================================================

export type ConciergeWidgetType = 'floating_button' | 'embedded' | 'modal';

export type DeviceType = 'desktop' | 'mobile' | 'tablet';

export type PageType = 'pdp' | 'brand' | 'brandclub' | 'search' | 'category' | 'homepage' | 'other';

export type ConversationAccessPointType =
  | 'search_bar' // User typed in the search input
  | 'feature_image' // User clicked a featured image/banner
  | 'quick_prompts' // User clicked a quick prompt/suggested question
  | 'floating_button' // User opened widget via floating button
  | 'suggested_pill' // User clicked a suggested pill on minimized widget
  | 'follow_up_question' // User clicked a follow-up question
  | 'conversation_starter' // User clicked a conversation starter on PDP
  | 'pdp_ask_question' // User clicked "Ask a question" on PDP
  | 'concierge_home'; // User started conversation from the dedicated concierge page

export type ProductClickSource =
  | 'carousel' // Product in a horizontal carousel
  | 'featured' // Featured product widget
  | 'search_results' // Product in search results grid
  | 'comparison' // Product in comparison view
  | 'buy_box' // Product in buy box
  | 'highlight' // Inline product highlight card
  | 'widget'; // MF widget click without a more specific source (legacy default)

// ============================================================================
// Event Property Interfaces
// ============================================================================

/**
 * WIDGET_IMPRESSION Event Properties
 *
 * Track when a concierge widget becomes visible to the user.
 * Fire this event when:
 * - Floating button is rendered on the page
 * - Embedded widget becomes visible (use IntersectionObserver)
 * - Modal is opened
 *
 * Deduplication: Consider tracking once per page load or using a time-based
 * throttle (e.g., once per 30 seconds) to avoid excessive events.
 */
export interface WidgetImpressionProperties {
  // Required
  pageUrl: string;
  pageName: string;
  widgetType: ConciergeWidgetType;
  sessionId: string;
  appName: string;

  // Optional
  stacklineSku?: string;
  brandId?: number;
  appVersion?: 'v2';
  appId?: string;
}

/**
 * WIDGET_SESSION_START Event Properties
 *
 * Track the first time a user sees the widget in a session.
 * Fire this event ONCE per session (use sessionStorage or similar to track).
 *
 * Implementation:
 * 1. Check if 'concierge_session_started' flag exists in sessionStorage
 * 2. If not, fire the event and set the flag
 * 3. If yes, skip (already tracked for this session)
 */
export interface WidgetSessionStartProperties {
  // Required
  sessionId: string;
  appName: string;
  referrer: string;
  deviceType: DeviceType;

  // Optional
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  pageUrl?: string;
  appVersion?: 'v2';
  appId?: string;
}

/**
 * CONVERSATION_ACCESS_POINT Event Properties
 *
 * Track where users initiate conversations from.
 * Fire this event when a user sends their first message from a specific entry point.
 *
 * Implementation:
 * - Track the source when user interacts (click on pill, type in search bar, etc.)
 * - Fire the event when the message is actually sent
 * - Include whether this started a new conversation or continued an existing one
 */
export interface ConversationAccessPointProperties {
  // Required
  accessPoint: ConversationAccessPointType;
  conversationId: string;
  pageUrl: string;

  // Optional
  stacklineSku?: string;
  appVersion?: 'v2';
  appId?: string;
  query?: string; // Truncate to 200 chars
  isNewConversation?: boolean;
}

/**
 * PAGE_CONTEXT Event Properties
 *
 * Track what page the user is on during a conversation.
 * Fire this event:
 * - When a conversation starts (after CONVERSATION_ACCESS_POINT)
 * - When user navigates to a different page while widget is open
 *
 * Implementation:
 * - Listen for route changes while conversationId is active
 * - Fire on initial conversation start
 */
export interface PageContextProperties {
  // Required
  conversationId: string;
  pageUrl: string;
  pageName: string;
  pageType: PageType;

  // Optional
  stacklineSku?: string;
  appVersion?: 'v2';
  appId?: string;
  brandId?: number;
  categoryId?: number;
  searchQuery?: string;
}

/**
 * PRODUCT_CLICK Event Properties
 *
 * Track when users click on products shown in the concierge.
 * Fire this event when a user clicks a product link/card.
 *
 * Implementation:
 * - Add onClick handler to product cards in carousels, featured products, etc.
 * - Track the position in the list for ranking analysis
 * - Include product details for segmentation
 */
export interface ProductClickProperties {
  /** Same opaque touch as the qualifying `product_suggested` event — a retry never
   *  generates a new one, or the cart would carry a token no raw event corroborates. */
  conciergeTouchId?: string;
  // Required
  conversationId: string;
  stacklineSku: string;
  productTitle: string;
  clickSource: ProductClickSource;

  // Optional
  productPosition?: number;
  retailerId?: number;
  retailPrice?: number;
  hasReward?: boolean;
  rewardAmount?: number;
  messageIndex?: number;
  appVersion?: 'v2';
  appId?: string;
}

/**
 * PRODUCT_SUGGESTED Event Properties
 *
 * Fired when a product is shown to the user (carousel, inline highlight, etc.).
 * Aligns with server-side ProductCarouselsTool payloads for warehouse joins.
 */
export interface ProductSuggestedProperties {
  /** Opaque per-conversation attribution touch, mirrored onto the Shopify cart. */
  conciergeTouchId?: string;
  conversationId: string;
  stacklineSku: string;
  /** Display title (duplicate productTitle for parity with server Mixpanel payloads) */
  title?: string;
  productTitle?: string;
  retailerName?: string;
  retailerId?: number;
  retailerSku?: string;
  retailerParentSku?: string;
  brandId?: number;
  brandName?: string;
  categoryId?: number;
  categoryName?: string;
  groupId?: number;
  groupName?: string;
  subCategoryId?: number;
  subCategoryName?: string;
  retailPrice?: number;
  /** User message that led to this suggestion, when available */
  prompt?: string;
  appName?: string;
  appVersion?: 'v2';
  appId?: string;
  /** JSON-stringified brand id list, matching server `brandIds` */
  brandIds?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the device type based on screen width
 */
export function getDeviceType(): DeviceType {
  if (typeof window === 'undefined') return 'desktop';

  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

/**
 * Get page name from pathname
 */
export function getPageNameFromPath(pathname: string): string {
  if (pathname === '/' || pathname === '') return 'Homepage';
  if (pathname.includes('/pdp/') || pathname.includes('/p/')) return 'Product Detail Page';
  if (pathname.includes('/brand/')) return 'Brand Page';
  if (pathname.includes('/search')) return 'Search Results';
  if (pathname.includes('/category/')) return 'Category Page';
  if (pathname.includes('/brandclub')) return 'Brandclub Page';
  return 'Other';
}

/**
 * Get page type from pathname
 */
export function getPageTypeFromPath(pathname: string): PageType {
  if (pathname === '/' || pathname === '') return 'homepage';
  if (pathname.includes('/pdp/') || pathname.includes('/p/')) return 'pdp';
  if (pathname.includes('/brand/')) return 'brand';
  if (pathname.includes('/search')) return 'search';
  if (pathname.includes('/category/')) return 'category';
  if (pathname.includes('/brandclub')) return 'brandclub';
  return 'other';
}

/**
 * Extract UTM parameters from URL
 */
export function getUTMParams(): { utmSource?: string; utmMedium?: string; utmCampaign?: string } {
  if (typeof window === 'undefined') return {};

  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get('utm_source') || undefined,
    utmMedium: params.get('utm_medium') || undefined,
    utmCampaign: params.get('utm_campaign') || undefined,
  };
}

/**
 * Truncate a string to a maximum length
 */
export function truncateQuery(query: string, maxLength: number = 200): string {
  if (query.length <= maxLength) return query;
  return query.substring(0, maxLength) + '...';
}
